import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

// Signature details for consent sections
export interface IfspConsentSignatureType {
  id: string;
  signatureId: string | null;
  partitionKey: string | null;
  rowKey: string | null;
  isUpdated: boolean;
  date: string | null;
  signature: string | null;
  verbalConsent: boolean;
  paperConsent: boolean;
}

export type ReviewHistoryType = {
  id: string | null;
  reviewedById: string;
  name: string;
  discipline: string;
  date: string | null | Dayjs;
};

export interface FamilyRightsNoticeOptionType {
  id: string | null;
  label: string | null;
}

// Consent record
export interface ConsentType {
  id: string;
  ifspId: string;
  isEligibleUntilThirdBirthday: string | null;
  eligibilityEvaluationTool: string | null;
  isEligibleForComment: string | null;
  assessmentCompletedOn: string | null;
  reestablishmentDate: string | null;
  participatedInIfspDevelopment: boolean | null;
  agreeDisagreeService: string | null;
  listExceptionsComment: string | null;
  consentToAccessInsurance: boolean | null;
  changesToServiceDeliveryComment: string | null;
  isConsentSkipped: boolean | null;
  completedOn: string | null;
  isAdminAmendment: boolean | null;
  consentType: string | null;
  familyRightsNotice: FamilyRightsNoticeOptionType | null;
  consentForServiceSignature: IfspConsentSignatureType[];
  consentToAccessInsuranceSignature: IfspConsentSignatureType[];
  adminAmendmentSignature: IfspConsentSignatureType;
  latestConsent: boolean;
  ifspReviewHistory: ReviewHistoryType | null;
  uiVersion?: number | null;
}

// Service Plan details inside Service Delivery
export interface ServicePlanType {
  id: string;
  eiService: string;
  providedBy: string;
  locationOfService: string;
  frequency: number | null;
  period: string;
  length: number | null;
  intensity: string;
  durationOfService: string;
  startDate: string;
  endDate: string;
  reasonForLate: string;
  tbrServicePlanId: number;
  tbrUpdateRequired: boolean;
}

// Service Delivery block
export interface ServiceDeliveryType {
  ifspId: string;
  id: string;
  eiServiceJustificationComment: string | null;
  serviceSupportComment: string | null;
  serviceSupportNeededComment: string | null;
  serviceDeliveryType: string | null;
  servicePlan: ServicePlanType[];
}

export interface IfspSDPAndConsentType {
  ifspId: string | null;
  serviceDelivery: ServiceDeliveryType[];
  consent: ConsentType[];
  lockWithoutConsentSignature: boolean;
  isLocked: boolean;
  heldBy: string | null;
  isCurrentlyHeld: boolean;
  referralContactDate: string | null;
  displayBanner: boolean;
}

// Complete IFSP SDP + Consent structure
export interface IfspSDPAndConsentStateType {
  sdpAndConsent: IfspSDPAndConsentType;
  originalSdpAndConsent: IfspSDPAndConsentType;
}

const createServiceDelivery = () => ({
  ifspId: '',
  id: '',
  eiServiceJustificationComment: '',
  serviceSupportComment: '',
  serviceSupportNeededComment: '',
  serviceDeliveryType: '',
  servicePlan: [],
});

export const createConsentSignature = () => ({
  id: '',
  signatureId: '',
  partitionKey: '',
  rowKey: '',
  isUpdated: false,
  date: '',
  signature: '',
  verbalConsent: false,
  paperConsent: false,
});

export const createConsent = () => ({
  id: '',
  ifspId: '',
  isEligibleUntilThirdBirthday: '',
  eligibilityEvaluationTool: '',
  isEligibleForComment: '',
  assessmentCompletedOn: '',
  reestablishmentDate: '',
  participatedInIfspDevelopment: false,
  agreeDisagreeService: '',
  listExceptionsComment: '',
  consentToAccessInsurance: false,
  changesToServiceDeliveryComment: '',
  isConsentSkipped: false,
  completedOn: '',
  isAdminAmendment: false,
  consentType: '',
  latestConsent: false,
  familyRightsNotice: {
    id: '',
    label: '',
  },
  consentForServiceSignature: [
    createConsentSignature(),
    createConsentSignature(),
  ],
  consentToAccessInsuranceSignature: [
    createConsentSignature(),
    createConsentSignature(),
  ],
  adminAmendmentSignature: createConsentSignature(),
  ifspReviewHistory: {
    id: '',
    reviewedById: '',
    name: '',
    discipline: '',
    date: null,
  },
  uiVersion: 2,
});

const createSdpAndConsent = () => ({
  ifspId: null,
  serviceDelivery: [createServiceDelivery()],
  consent: [createConsent()],
  lockWithoutConsentSignature: false,
  isLocked: false,
  heldBy: null,
  displayBanner: false,
  isCurrentlyHeld: false,
  referralContactDate: null,
});

export const initialIfspSDPAndConsent: IfspSDPAndConsentStateType = {
  sdpAndConsent: createSdpAndConsent(),
  originalSdpAndConsent: createSdpAndConsent(),
};

const ifspSDPAndConsentSlice = createSlice({
  name: 'ifspSDPAndConsent',
  initialState: initialIfspSDPAndConsent,
  reducers: {
    setIfspSDPAndConsent(state, action: PayloadAction<IfspSDPAndConsentType>) {
      state.sdpAndConsent = action.payload;
      state.originalSdpAndConsent = action.payload;
    },
    updateIfspSDPAndConsent(
      state,
      action: PayloadAction<Partial<IfspSDPAndConsentType>>,
    ) {
      state.sdpAndConsent = {
        ...state.sdpAndConsent,
        ...action.payload,
      };
    },
    updateIfspOriginalSDPAndConsent(
      state,
      action: PayloadAction<Partial<IfspSDPAndConsentType>>,
    ) {
      state.originalSdpAndConsent = {
        ...state.originalSdpAndConsent,
        ...action.payload,
      };
    },
    resetIfspSDPAndConsent() {
      return initialIfspSDPAndConsent;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('RESET_REDUX_APP_STATES', () => initialIfspSDPAndConsent);
  },
});

export const {
  setIfspSDPAndConsent,
  updateIfspSDPAndConsent,
  updateIfspOriginalSDPAndConsent,
  resetIfspSDPAndConsent,
} = ifspSDPAndConsentSlice.actions;
export default ifspSDPAndConsentSlice.reducer;
